import React, { useState } from 'react';
import {
  Box,
  Button,
  Typography,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Grid,
  Card,
  CardContent,
  Alert,
  Snackbar,
  InputAdornment,
  Avatar,
  Divider,
  Paper
} from '@mui/material';
import {
  TransferWithinAStation as TransferIcon,
  Person as PersonIcon,
  Business as BusinessIcon,
  ArrowBack as BackIcon,
  Save as SaveIcon
} from '@mui/icons-material';
import { useHistory } from 'react-router-dom';

// Mock associates data
const mockAssociates = [
  { id: 'AS001', name: 'John Smith', department: 'Sales' },
  { id: 'AS002', name: 'Sarah Johnson', department: 'Sales' },
  { id: 'AS003', name: 'Mike Wilson', department: 'Sales' },
  { id: 'AS004', name: 'Emily Davis', department: 'Sales' },
  { id: 'AS005', name: 'David Brown', department: 'Sales' },
  { id: 'AS006', name: 'Lisa Anderson', department: 'Sales' },
  { id: 'AS007', name: 'Robert Taylor', department: 'Sales' },
  { id: 'AS008', name: 'Jennifer White', department: 'Sales' },
];

// Mock leads data
const mockLeads = [
  { id: 'LD001', name: 'ABC Company Lead', status: 'Active' },
  { id: 'LD002', name: 'XYZ Corporation Lead', status: 'Active' },
  { id: 'LD003', name: 'Tech Startup Lead', status: 'Active' },
  { id: 'LD004', name: 'Manufacturing Lead', status: 'Active' },
  { id: 'LD005', name: 'Retail Chain Lead', status: 'Active' },
];

const TransferProcess = () => {
  const history = useHistory();
  console.log('TransferProcess component loaded');
  
  const [transferForm, setTransferForm] = useState({
    leadId: '',
    fromAssociateId: '',
    toAssociateId: '',
    reason: '',
  });
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });

  const handleTransferFormChange = (field, value) => {
    setTransferForm(prev => ({
      ...prev,
      [field]: value,
    }));
  };

  const handleTransferSubmit = () => {
    if (!transferForm.leadId || !transferForm.fromAssociateId || !transferForm.toAssociateId || !transferForm.reason) {
      setSnackbar({
        open: true,
        message: 'Please fill in all required fields',
        severity: 'error',
      });
      return;
    }

    if (transferForm.fromAssociateId === transferForm.toAssociateId) {
      setSnackbar({
        open: true,
        message: 'From and To associates cannot be the same',
        severity: 'error',
      });
      return;
    }

    // Here you would typically make an API call to submit the transfer
    setSnackbar({
      open: true,
      message: 'Transfer request submitted successfully',
      severity: 'success',
    });

    // Reset form after successful submission
    setTransferForm({
      leadId: '',
      fromAssociateId: '',
      toAssociateId: '',
      reason: '',
    });
  };

  const handleBack = () => {
    history.goBack();
  };

  const handleSnackbarClose = () => {
    setSnackbar(prev => ({ ...prev, open: false }));
  };

  return (
    <Box sx={{ p: 3 }}>
      {/* Debug Alert */}
      {/* <Alert severity="info" sx={{ mb: 2 }}>
        TransferProcess component is loaded and working!
      </Alert> */}
      
      {/* Header Section */}
      <Box sx={{ mb: 3 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
          <Button
            startIcon={<BackIcon />}
            onClick={handleBack}
            sx={{
              mr: 2,
              color: '#536DFE',
              textTransform: 'none',
              '&:hover': {
                backgroundColor: 'rgba(83, 109, 254, 0.04)'
              }
            }}
          >
            Back
          </Button>
          <TransferIcon sx={{ mr: 1, color: '#536DFE', fontSize: 28 }} />
          <Typography variant="h4" sx={{ fontWeight: 600, color: '#333' }}>
            Transfer Associate Process
          </Typography>
        </Box>
        <Typography variant="body1" sx={{ color: '#666', mb: 3 }}>
          Transfer a lead from one associate to another. Please provide all required information below.
        </Typography>
      </Box>

      {/* Transfer Form */}
      <Card sx={{ boxShadow: '0 2px 12px rgba(0,0,0,0.1)', borderRadius: 2 }}>
        <CardContent sx={{ p: 3 }}>
          <Grid container spacing={3}>
            {/* Lead Selection */}
            <Grid item xs={12}>
              <Typography variant="h6" sx={{ mb: 2, color: '#333', fontWeight: 600 }}>
                Lead Information
              </Typography>
              <FormControl fullWidth>
                <InputLabel>Select Lead *</InputLabel>
                <Select
                  value={transferForm.leadId}
                  onChange={(e) => handleTransferFormChange('leadId', e.target.value)}
                  sx={{ borderRadius: 2 }}
                >
                  {mockLeads.map((lead) => (
                    <MenuItem key={lead.id} value={lead.id}>
                      <Box sx={{ display: 'flex', alignItems: 'center', width: '100%' }}>
                        <Avatar sx={{ mr: 2, backgroundColor: '#536DFE', width: 32, height: 32 }}>
                          <BusinessIcon sx={{ fontSize: 16 }} />
                        </Avatar>
                        <Box sx={{ flex: 1 }}>
                          <Typography variant="body2" sx={{ fontWeight: 500 }}>
                            {lead.name}
                          </Typography>
                          <Typography variant="caption" sx={{ color: '#666' }}>
                            {lead.id} • {lead.status}
                          </Typography>
                        </Box>
                      </Box>
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>

            <Divider sx={{ my: 2, width: '100%' }} />

            {/* Associate Selection */}
            <Grid item xs={12}>
              <Typography variant="h6" sx={{ mb: 2, color: '#333', fontWeight: 600 }}>
                Associate Transfer Details
              </Typography>
            </Grid>

            <Grid item xs={12} md={6}>
              <FormControl fullWidth>
                <InputLabel>From Associate *</InputLabel>
                <Select
                  value={transferForm.fromAssociateId}
                  onChange={(e) => handleTransferFormChange('fromAssociateId', e.target.value)}
                  sx={{ borderRadius: 2 }}
                >
                  {mockAssociates.map((associate) => (
                    <MenuItem key={associate.id} value={associate.id}>
                      <Box sx={{ display: 'flex', alignItems: 'center', width: '100%' }}>
                        <Avatar sx={{ mr: 2, backgroundColor: '#FF5C93', width: 32, height: 32 }}>
                          <PersonIcon sx={{ fontSize: 16 }} />
                        </Avatar>
                        <Box sx={{ flex: 1 }}>
                          <Typography variant="body2" sx={{ fontWeight: 500 }}>
                            {associate.name}
                          </Typography>
                          <Typography variant="caption" sx={{ color: '#666' }}>
                            {associate.id} • {associate.department}
                          </Typography>
                        </Box>
                      </Box>
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>

            <Grid item xs={12} md={6}>
              <FormControl fullWidth>
                <InputLabel>To Associate *</InputLabel>
                <Select
                  value={transferForm.toAssociateId}
                  onChange={(e) => handleTransferFormChange('toAssociateId', e.target.value)}
                  sx={{ borderRadius: 2 }}
                >
                  {mockAssociates.map((associate) => (
                    <MenuItem key={associate.id} value={associate.id}>
                      <Box sx={{ display: 'flex', alignItems: 'center', width: '100%' }}>
                        <Avatar sx={{ mr: 2, backgroundColor: '#3CD4A0', width: 32, height: 32 }}>
                          <PersonIcon sx={{ fontSize: 16 }} />
                        </Avatar>
                        <Box sx={{ flex: 1 }}>
                          <Typography variant="body2" sx={{ fontWeight: 500 }}>
                            {associate.name}
                          </Typography>
                          <Typography variant="caption" sx={{ color: '#666' }}>
                            {associate.id} • {associate.department}
                          </Typography>
                        </Box>
                      </Box>
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>

            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Transfer Reason *"
                multiline
                rows={4}
                value={transferForm.reason}
                onChange={(e) => handleTransferFormChange('reason', e.target.value)}
                placeholder="Please provide a detailed reason for this transfer..."
                sx={{ borderRadius: 2 }}
                helperText="Provide a clear explanation for why this transfer is necessary"
              />
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      {/* Action Buttons */}
      <Box sx={{ mt: 3, display: 'flex', justifyContent: 'flex-end', gap: 2 }}>
        <Button
          variant="outlined"
          onClick={handleBack}
          sx={{
            borderRadius: 2,
            textTransform: 'none',
            px: 3,
            borderColor: '#536DFE',
            color: '#536DFE',
            '&:hover': {
              borderColor: '#3F51B5',
              backgroundColor: 'rgba(83, 109, 254, 0.04)'
            }
          }}
        >
          Cancel
        </Button>
        <Button
          variant="contained"
          startIcon={<SaveIcon />}
          onClick={handleTransferSubmit}
          sx={{
            backgroundColor: '#536DFE',
            '&:hover': { backgroundColor: '#3F51B5' },
            borderRadius: 2,
            textTransform: 'none',
            px: 3
          }}
        >
          Submit Transfer
        </Button>
      </Box>

      {/* Transfer Summary */}
      {transferForm.leadId && transferForm.fromAssociateId && transferForm.toAssociateId && (
        <Card sx={{ mt: 3, boxShadow: '0 2px 12px rgba(0,0,0,0.1)', borderRadius: 2 }}>
          <CardContent>
            <Typography variant="h6" sx={{ mb: 2, color: '#333', fontWeight: 600 }}>
              Transfer Summary
            </Typography>
            <Paper sx={{ p: 2, backgroundColor: '#F6F7FF', borderRadius: 2 }}>
              <Grid container spacing={2}>
                <Grid item xs={12} md={4}>
                  <Box sx={{ display: 'flex', alignItems: 'center' }}>
                    <Avatar sx={{ mr: 2, backgroundColor: '#536DFE', width: 32, height: 32 }}>
                      <BusinessIcon sx={{ fontSize: 16 }} />
                    </Avatar>
                    <Box>
                      <Typography variant="body2" sx={{ fontWeight: 500 }}>
                        {mockLeads.find(l => l.id === transferForm.leadId)?.name}
                      </Typography>
                      <Typography variant="caption" sx={{ color: '#666' }}>
                        Lead ID: {transferForm.leadId}
                      </Typography>
                    </Box>
                  </Box>
                </Grid>
                <Grid item xs={12} md={4}>
                  <Box sx={{ display: 'flex', alignItems: 'center' }}>
                    <Avatar sx={{ mr: 2, backgroundColor: '#FF5C93', width: 32, height: 32 }}>
                      <PersonIcon sx={{ fontSize: 16 }} />
                    </Avatar>
                    <Box>
                      <Typography variant="body2" sx={{ fontWeight: 500 }}>
                        {mockAssociates.find(a => a.id === transferForm.fromAssociateId)?.name}
                      </Typography>
                      <Typography variant="caption" sx={{ color: '#666' }}>
                        From Associate
                      </Typography>
                    </Box>
                  </Box>
                </Grid>
                <Grid item xs={12} md={4}>
                  <Box sx={{ display: 'flex', alignItems: 'center' }}>
                    <Avatar sx={{ mr: 2, backgroundColor: '#3CD4A0', width: 32, height: 32 }}>
                      <PersonIcon sx={{ fontSize: 16 }} />
                    </Avatar>
                    <Box>
                      <Typography variant="body2" sx={{ fontWeight: 500 }}>
                        {mockAssociates.find(a => a.id === transferForm.toAssociateId)?.name}
                      </Typography>
                      <Typography variant="caption" sx={{ color: '#666' }}>
                        To Associate
                      </Typography>
                    </Box>
                  </Box>
                </Grid>
              </Grid>
            </Paper>
          </CardContent>
        </Card>
      )}

      {/* Snackbar for notifications */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={handleSnackbarClose}
      >
        <Alert 
          onClose={handleSnackbarClose} 
          severity={snackbar.severity}
          sx={{ width: '100%' }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default TransferProcess;
